import { XMarkIcon } from "@heroicons/react/20/solid";
import { useTranslation } from "react-i18next";
import IconButton from "../../../ui/buttons/IconButton";
import Field from "../../../ui/forms/Field";
import { chipClassName } from "../../../ui/styles/chipStyles";
import { splitTyped, withAddresses } from "./emailAddresses";

interface EmailAddressesFieldProps {
  addresses: readonly string[];
  /** The address still being typed. */
  typing: string;
  onChange: (addresses: readonly string[], typing: string) => void;
  /** Under the field: what accepting brings. */
  hint: string;
  disabled: boolean;
}

/**
 * One or several email addresses (R3-4): each typed or pasted address becomes a chip with its
 * own ×, closed by a space, comma, semicolon, Enter or leaving the field. Backspace in the empty
 * field takes the last one back.
 */
export default function EmailAddressesField({ addresses, typing, onChange, hint, disabled }: EmailAddressesFieldProps) {
  const { t } = useTranslation();
  const type = (text: string) => {
    const { closed, typing: rest } = splitTyped(text);
    onChange(withAddresses(addresses, closed), rest);
  };
  return (
    <Field label={t("access.invite.addresses")} hint={hint}>
      {(control) => (
        <div className="app-input flex w-full flex-wrap items-center gap-1">
          {addresses.map((address) => (
            <span key={address} className={chipClassName("neutral", "regular")}>
              {address}
              <IconButton label={t("access.invite.removeAddress", { address })} disabled={disabled} className="h-4 w-4"
                onClick={() => { onChange(addresses.filter((other) => other !== address), typing); }}>
                <XMarkIcon className="h-3.5 w-3.5" aria-hidden="true" />
              </IconButton>
            </span>
          ))}
          <input {...control} type="text" inputMode="email" autoComplete="off" spellCheck={false} disabled={disabled}
            className="min-w-[12rem] flex-1 bg-transparent outline-none" value={typing}
            onChange={(event) => { type(event.target.value); }}
            onBlur={() => { if (typing.trim() !== "") type(`${typing} `); }}
            onKeyDown={(event) => {
              if (event.key === "Enter" && typing.trim() !== "") {
                event.preventDefault();
                type(`${typing} `);
              } else if (event.key === "Backspace" && typing === "" && addresses.length > 0) {
                onChange(addresses.slice(0, -1), "");
              }
            }} />
        </div>
      )}
    </Field>
  );
}
